import React, { useState } from 'react';
import { useLocation } from "react-router-dom";
import LabsAppBar from "../../components/LabsAppBar";
import { ActionButton } from '../../components/LabsAppBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import {
  CalendarDays, ChevronLeft, ChevronRight, RefreshCw, LayoutDashboard, ArrowUp, ArrowDown,
  List, Clock, BarChart2, Settings, Users, TrendingUp, TrendingDown
} from 'lucide-react';
import clsx from 'clsx';

// Mock Data
const mockMetricData = [
  { name: 'Mon', uv: 4000, pv: 2400, amt: 2400 },
  { name: 'Tue', uv: 3000, pv: 1398, amt: 2210 },
  { name: 'Wed', uv: 2000, pv: 9800, amt: 2290 },
  { name: 'Thu', uv: 2780, pv: 3908, amt: 2000 },
  { name: 'Fri', uv: 1890, pv: 4800, amt: 2181 },
  { name: 'Sat', uv: 2390, pv: 3800, amt: 2500 },
  { name: 'Sun', uv: 3490, pv: 4300, amt: 2100 },
];

const mockPipelineData = [
  { date: 'Nov 11', 'NessaLab Prod Web': 10000, 'NessaLab Prod iOS': 4000, 'NessaLab Prod Android': 2000, 'Staging Web': 1000 },
  { date: 'Nov 12', 'NessaLab Prod Web': 12000, 'NessaLab Prod iOS': 5000, 'NessaLab Prod Android': 2500, 'Staging Web': 1200 },
  { date: 'Nov 13', 'NessaLab Prod Web': 8000, 'NessaLab Prod iOS': 3000, 'NessaLab Prod Android': 1500, 'Staging Web': 800 },
  { date: 'Nov 14', 'NessaLab Prod Web': 15000, 'NessaLab Prod iOS': 6000, 'NessaLab Prod Android': 3000, 'Staging Web': 1500 },
  { date: 'Nov 15', 'NessaLab Prod Web': 11000, 'NessaLab Prod iOS': 4500, 'NessaLab Prod Android': 2200, 'Staging Web': 1100 },
  { date: 'Nov 16', 'NessaLab Prod Web': 9000, 'NessaLab Prod iOS': 3500, 'NessaLab Prod Android': 1800, 'Staging Web': 900 },
  { date: 'Nov 17', 'NessaLab Prod Web': 13000, 'NessaLab Prod iOS': 5500, 'NessaLab Prod Android': 2700, 'Staging Web': 1300 },
  { date: 'Nov 18', 'NessaLab Prod Web': 14000, 'NessaLab Prod iOS': 5800, 'NessaLab Prod Android': 2900, 'Staging Web': 1400 },
];

const mockTrafficData = [
  { integration: 'Facebook', traffic: '86.9k', trafficPercent: '45.54%', errors: 0, progress: 80 },
  { integration: 'Adobe Analytics', traffic: '51.2k', trafficPercent: '32.24%', errors: 1, progress: 60 },
  { integration: 'Snapchat', traffic: '33.1k', trafficPercent: '21.00%', errors: 0, progress: 40 },
  { integration: 'Adobe Analytics', traffic: '16.9k', trafficPercent: '12.81%', errors: 2, progress: 20 },
  { integration: 'Facebook', traffic: '6.2k', trafficPercent: '5.54%', errors: 0, progress: 10 },
  { integration: 'Facebook', traffic: '2', trafficPercent: '0.01%', errors: 0, progress: 1 },
];

const mockClusterData = [
  { cluster: 'NessaLab-US-EAST-01', traffic: '86.9k', trafficPercent: '45.54%', errors: 0, progress: 80 },
  { cluster: 'NessaLab-US-WEST-01', traffic: '51.2k', trafficPercent: '32.24%', errors: 1, progress: 60 },
  { cluster: 'NessaLab-US-WEST-02', traffic: '33.1k', trafficPercent: '21.00%', errors: 0, progress: 40 },
  { cluster: 'NessaLab-EU-EAST', traffic: '16.9k', trafficPercent: '12.81%', errors: 2, progress: 20 },
  { cluster: 'NessaLab-EU-WEST', traffic: '6.2k', trafficPercent: '5.54%', errors: 0, progress: 10 },
  { cluster: 'NessaLab-ASIA', traffic: '2', trafficPercent: '0.01%', errors: 0, progress: 1 },
];

const Icon = ({ name, className }: { name: string; className?: string }) => {
  const LucideIcon = (
    {
      LayoutDashboard: LayoutDashboard,
      CalendarDays: CalendarDays,
      ChevronLeft: ChevronLeft,
      ChevronRight: ChevronRight,
      RefreshCw: RefreshCw,
      ArrowUp: ArrowUp,
      ArrowDown: ArrowDown,
      List: List,
      Clock: Clock,
      BarChart2: BarChart2,
      Settings: Settings,
      Users: Users,
      TrendingUp: TrendingUp,
      TrendingDown: TrendingDown,
    } as { [key: string]: React.ElementType }
  )[name];
  return LucideIcon ? <LucideIcon className={className} /> : null;
};

const DashboardPage: React.FC = () => {
  const location = useLocation();
  const [timeframe, setTimeframe] = useState('day'); // 'hour' or 'day'
  const [hoveredBar, setHoveredBar] = useState<{ date: string; value: number; name: string } | null>(null);

  let breadcrumbs = [];
  let actions: ActionButton[] = [];
  let pageTitle = '';
  let pageIcon: string | undefined;

  switch (location.pathname) {
    case '/labs/dashboard':
      pageTitle = 'Dashboard';
      pageIcon = 'HomeGraph';
      actions = [];
      break;
    default:
      pageTitle = 'Dashboard';
      pageIcon = 'HomeGraph';
      actions = [];
      break;
  }

  breadcrumbs = [{ name: pageTitle, iconName: pageIcon }];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-700 p-2 border border-gray-300 dark:border-gray-600 rounded shadow-lg text-sm">
          <p className="font-semibold text-gray-900 dark:text-white">{`Date: ${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={`item-${index}`} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // const renderSparkline = (data: any[]) => (
  //   <ResponsiveContainer width="100%" height={50}>
  //     <LineChart data={data}>
  //       <Line type="monotone" dataKey="uv" stroke="#3b82f6" strokeWidth={2} dot={false} />
  //     </LineChart>
  //   </ResponsiveContainer>
  // );

  const renderAreaChart = (data: any[]) => (
    <ResponsiveContainer width="100%" height={50}>
      <AreaChart data={data}>
        <Area type="monotone" dataKey="uv" stroke="#3b82f6" fill="#bfdbfe" strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );

  return (
    <div className="flex flex-col h-full bg-gray-100 dark:bg-gray-900">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <button className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
              <Icon name="ChevronLeft" className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-1 text-gray-700 dark:text-gray-200 font-medium">
              <Icon name="CalendarDays" className="w-4 h-4" />
              <span>This Week</span>
            </div>
            <button className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
              <Icon name="ChevronRight" className="w-5 h-5" />
            </button>
            <span className="text-gray-500 dark:text-gray-400 text-sm ml-4">Nov 11, 11:30 am — Nov 18, 11:30 am • compared with 1 week ago</span>
          </div>
          <button className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
            <Icon name="RefreshCw" className="w-5 h-5" />
          </button>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 flex flex-col justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Events Received</p>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">3.17M</h3>
            </div>
            <div className="mt-4">
              {renderAreaChart(mockMetricData)}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 flex flex-col justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Forwarder Throughput</p>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">13.2 G</h3>
            </div>
            <div className="mt-4">
              {renderAreaChart(mockMetricData)}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 flex flex-col justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Ingestion Error Rate</p>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">0.14 %</h3>
            </div>
            <div className="mt-4">
              {renderAreaChart(mockMetricData)}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 flex flex-col justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Delivery Error Rate</p>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">0.03 %</h3>
            </div>
            <div className="mt-4">
              {renderAreaChart(mockMetricData)}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Total Active</p>
            <div className="flex items-center justify-center mt-1">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white">12,584</h4>
              <span className="text-green-500 text-sm ml-2 flex items-center"><Icon name="ArrowUp" className="w-4 h-4 mr-1" />5%</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Sessions</p>
            <div className="flex items-center justify-center mt-1">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white">2,120</h4>
              <span className="text-red-500 text-sm ml-2 flex items-center"><Icon name="ArrowDown" className="w-4 h-4 mr-1" />1%</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Average Session</p>
            <div className="flex items-center justify-center mt-1">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white">0:09:23</h4>
              <span className="text-green-500 text-sm ml-2 flex items-center"><Icon name="ArrowUp" className="w-4 h-4 mr-1" />3%</span>
            </div>
          </div>
        </div>

        {/* Traffic by Pipelines Chart */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Traffic by Pipelines <span className="text-gray-500 dark:text-gray-400">(Top 10)</span></h2>
            <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 rounded-md p-1">
              <button
                className={clsx(
                  "px-3 py-1 rounded-md text-sm font-medium",
                  timeframe === 'hour' ? "bg-blue-500 text-white" : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                )}
                onClick={() => setTimeframe('hour')}
              >
                Per hour
              </button>
              <button
                className={clsx(
                  "px-3 py-1 rounded-md text-sm font-medium",
                  timeframe === 'day' ? "bg-blue-500 text-white" : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                )}
                onClick={() => setTimeframe('day')}
              >
                Per day
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={mockPipelineData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              onMouseMove={(state) => {
                if (state.isTooltipActive && state.activePayload && state.activePayload.length > 0) {
                  const dataPoint = state.activePayload[0].payload;
                  const total = state.activePayload.reduce((sum: number, entry: any) => sum + entry.value, 0);
                  setHoveredBar({ date: dataPoint.date, value: total, name: 'Total Traffic' });
                } else {
                  setHoveredBar(null);
                }
              }}
              onMouseLeave={() => setHoveredBar(null)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={{ stroke: '#e0e0e0' }} className="text-xs text-gray-600 dark:text-gray-400" />
              <YAxis tickLine={false} axisLine={{ stroke: '#e0e0e0' }} className="text-xs text-gray-600 dark:text-gray-400" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Bar dataKey="NessaLab Prod Web" stackId="a" fill="#3b82f6" /> {/* Tailwind blue-500 */}
              <Bar dataKey="NessaLab Prod iOS" stackId="a" fill="#60a5fa" /> {/* Tailwind blue-400 */}
              <Bar dataKey="NessaLab Prod Android" stackId="a" fill="#93c5fd" /> {/* Tailwind blue-300 */}
              <Bar dataKey="Staging Web" stackId="a" fill="#bfdbfe" /> {/* Tailwind blue-200 */}
            </BarChart>
          </ResponsiveContainer>
          {hoveredBar && (
            <div className="text-center mt-2 text-gray-700 dark:text-gray-300">
              <p className="font-semibold">{hoveredBar.date}</p>
              <p>{hoveredBar.name}: {hoveredBar.value}</p>
            </div>
          )}
        </div>

        {/* Traffic per Destination & Traffic by Cluster */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Traffic per Destination */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Traffic per Destination</h2>
              <div className="flex space-x-2">
                <button className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <Icon name="List" className="w-5 h-5" />
                </button>
                <button className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <Icon name="Clock" className="w-5 h-5" />
                </button>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">8.14</h3>
            <div className="space-y-4">
              {mockTrafficData.map((item, index) => (
                <div key={index} className="flex items-center text-sm">
                  <div className="w-1/4 text-gray-700 dark:text-gray-300">{item.integration}</div>
                  <div className="w-1/4">
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${item.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-1/4 text-right text-gray-700 dark:text-gray-300 font-medium">{item.traffic}</div>
                  <div className="w-1/6 text-right text-gray-500 dark:text-gray-400">{item.trafficPercent}</div>
                  <div className="w-1/12 text-right text-gray-700 dark:text-gray-300">{item.errors}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Traffic by Cluster */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Traffic by Cluster</h2>
              <div className="flex space-x-2">
                <button className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <Icon name="List" className="w-5 h-5" />
                </button>
                <button className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <Icon name="Clock" className="w-5 h-5" />
                </button>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">8.14</h3>
            <div className="space-y-4">
              {mockClusterData.map((item, index) => (
                <div key={index} className="flex items-center text-sm">
                  <div className="w-1/4 text-gray-700 dark:text-gray-300">{item.cluster}</div>
                  <div className="w-1/4">
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${item.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-1/4 text-right text-gray-700 dark:text-gray-300 font-medium">{item.traffic}</div>
                  <div className="w-1/6 text-right text-gray-500 dark:text-gray-400">{item.trafficPercent}</div>
                  <div className="w-1/12 text-right text-gray-700 dark:text-gray-300">{item.errors}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
